import { useEffect, useRef } from "react";
import { api } from "./api";
import { log } from "./log";

type Handler<T> = (payload: T) => void;
type StatusHandler = (connected: boolean) => void;

// Reconnect backoff. The browser's built-in EventSource reconnect can hammer a
// downed server; instead we close on error and reconnect ourselves with
// exponential backoff + jitter, capped, resetting once a connection opens.
const RECONNECT_BASE_MS = 1_000;
const RECONNECT_CAP_MS = 30_000;

let source: EventSource | null = null;
// A connect() is running but hasn't installed its EventSource yet. Minting the
// ticket is a round trip, so `source` stays null across it — and every consumer
// that mounted in the meantime used to see "no source" and start its own
// connect. The app mounts ~15 useSseEvent/useSseStatus consumers on a single
// page load, so one load minted ~15 single-use tickets and threw 14 away
// (the connectSeq guard below discards all but the last). That was a fifth of
// the ~70 API calls a page load made, and it counted against the per-IP rate
// limit like any other request (ENG-54607). This flag is the missing half of the
// "is a connection already coming?" question.
let connecting = false;
let subscriptionCount = 0;
let reconnectAttempts = 0;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
const statusHandlers = new Set<StatusHandler>();
// eventName → the listeners bound to the live EventSource, so we can rebind them
// after a reconnect (a new EventSource loses the old addEventListener calls).
const listeners = new Map<string, Set<EventListener>>();

function emitStatus(connected: boolean): void {
  for (const handler of statusHandlers) handler(connected);
}

// Full jitter: a random delay in [0, min(cap, base * 2^attempt)].
function backoffDelay(attempt: number): number {
  const ceiling = Math.min(RECONNECT_CAP_MS, RECONNECT_BASE_MS * 2 ** attempt);
  return Math.random() * ceiling;
}

// Monotonic connect sequence: minting the SSE ticket is async, so a connect
// attempt can be superseded (teardown, or a newer attempt) while its ticket is
// in flight — only the latest attempt may install its EventSource.
let connectSeq = 0;

async function connect(): Promise<void> {
  const seq = ++connectSeq;
  connecting = true;
  try {
    let url: string;
    try {
      // A fresh single-use ticket per (re)connect — the server refuses reuse, so
      // the URL must never be recycled across attempts.
      url = await api.createEventsUrl();
    } catch (err) {
      log.debug("SSE ticket mint failed:", err);
      emitStatus(false);
      scheduleReconnect();
      return;
    }
    // Superseded while the ticket was in flight (teardown or a newer connect).
    if (seq !== connectSeq || subscriptionCount === 0 || source) return;

    const es = new EventSource(url);
    source = es;
    es.onopen = () => {
      reconnectAttempts = 0;
      emitStatus(true);
    };
    es.onerror = () => {
      emitStatus(false);
      // Only we drive reconnection — kill the native retry loop (which would also
      // uselessly replay the same already-redeemed ticket).
      es.close();
      if (source !== es) return; // superseded (e.g. teardown)
      source = null;
      scheduleReconnect();
    };
    // Rebind every event listener registered by subscribeSse to the fresh source.
    for (const [eventName, set] of listeners) {
      for (const listener of set) es.addEventListener(eventName, listener);
    }
  } finally {
    // Cleared on EVERY exit — installed, superseded, or failed — so a connect
    // that came to nothing can't wedge the flag and block all later attempts.
    connecting = false;
  }
}

function scheduleReconnect(): void {
  if (reconnectTimer !== null || subscriptionCount === 0) return;
  const delay = backoffDelay(reconnectAttempts);
  reconnectAttempts += 1;
  log.debug(`SSE reconnect in ${Math.round(delay)}ms (attempt ${reconnectAttempts})`);
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    if (subscriptionCount === 0 || source || connecting) return;
    void connect();
  }, delay);
}

function ensureSource(): void {
  if (source || connecting) return;
  void connect();
}

// One shared ref-count for every consumer — event subscriptions AND status
// watchers. The EventSource lives while the count is > 0 and is torn down at 0;
// scheduleReconnect also early-returns at 0, so a consumer that didn't
// participate would hold a connection that never reconnects and never closes.
function acquire(): void {
  subscriptionCount += 1;
  ensureSource();
}

function release(): void {
  subscriptionCount -= 1;
  if (subscriptionCount === 0) teardown();
}

function teardown(): void {
  if (reconnectTimer !== null) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  reconnectAttempts = 0;
  if (source) {
    source.close();
    source = null;
  }
  emitStatus(false);
}

/** The primitive both hooks wrap: register a listener, ref-counting the shared
 *  EventSource. Exported for its own test — how many connections N simultaneous
 *  subscribers produce is the part that was silently wrong (see `connecting`) and
 *  that no React-level assertion would have caught. */
export function subscribeSse<T>(eventName: string, handler: Handler<T>): () => void {
  acquire();
  const listener: EventListener = (event) => {
    try {
      handler(JSON.parse((event as MessageEvent).data) as T);
    } catch (err) {
      // Surface malformed payloads instead of swallowing them — a broken event
      // shape is a real bug worth seeing, not silent no-op.
      log.warn(`Malformed SSE payload for "${eventName}":`, err);
    }
  };

  let set = listeners.get(eventName);
  if (!set) {
    set = new Set();
    listeners.set(eventName, set);
  }
  set.add(listener);

  source?.addEventListener(eventName, listener);

  return () => {
    set?.delete(listener);
    if (set && set.size === 0) listeners.delete(eventName);
    source?.removeEventListener(eventName, listener);
    release();
  };
}

export function useSseEvent<T>(eventName: string, handler: Handler<T>): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => subscribeSse<T>(eventName, (payload) => handlerRef.current(payload)), [eventName]);
}

export function useSseStatus(handler: StatusHandler): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    const wrapped = (connected: boolean) => handlerRef.current(connected);
    statusHandlers.add(wrapped);
    // Status watchers ref-count like event subscriptions: a component using
    // only useSseStatus still gets reconnects, and its unmount can close the
    // connection instead of leaking it.
    acquire();
    return () => {
      statusHandlers.delete(wrapped);
      release();
    };
  }, []);
}
